import { Controller, useForm } from "react-hook-form";
import { CreateProjectInput, DevelopmentType } from "@gql/sites-api/graphql";
import { DEVELOPMENT_TYPES, MAX_PROJECT_GOAL_LENGTH, MAX_PROJECT_NAME_LENGTH } from "./constants";
import { ImageUpload, ImageUploadStatus } from "react-migration/components/ImageUpload";
import { ProjectFormFields, projectSchema } from "./projectSchema";
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";

import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { ModalV2 } from "react-migration/components/ModalV2/ModalV2";
import { TextInputV2 } from "react-migration/components/TextInputV2";
import { Textarea } from "react-migration/components/Textarea";
import { ToggleGroupChips } from "./ToggleGroupChips";
import { useProjectUploadCallbacks } from "react-migration/domains/projects/hooks/useProjectUploadCallbacks";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useBetaFeatureFlag } from "react-migration/lib/user/useFeatureFlag";
import Feature from "src/js/stores/user/Feature";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";

export type ProjectData = {
  name: string;
  developmentType: string;
  goal?: string | null;
  image?: string | null;
  imageUrl?: string | null;
};

export interface ProjectModalProps {
  title: React.ReactNode;
  actionLabel: string;
  project?: ProjectData;
  handleAction: (input: CreateProjectInput) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
  titleError?: string;
  genericError?: boolean;
  onErrorDismiss?: () => void;
}

export const ProjectModal = ({
  title,
  actionLabel,
  project,
  handleAction,
  onClose,
  loading,
  titleError,
  genericError,
  onErrorDismiss,
}: ProjectModalProps) => {
  const { t } = useTranslation();

  const { control, handleSubmit, formState, watch, setError, clearErrors, setFocus } = useForm<ProjectFormFields>({
    defaultValues: {
      name: project?.name ?? "",
      developmentType: project?.developmentType
        ? (project.developmentType as DevelopmentType)
        : DevelopmentType.Residential,
      goal: project?.goal ?? "",
      image: project?.image ?? null,
      imageUrl: project?.imageUrl ?? null,
    },
    resolver: zodResolver(projectSchema),
    mode: "onChange",
  });

  const previousNameRef = useRef<string>();
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const name = watch("name");
  const [imageUploadStatus, setImageUploadStatus] = useState<ImageUploadStatus>("ready");
  const isImageUploading = imageUploadStatus === "uploading";
  const hasImageUploadError = imageUploadStatus === "error";
  const hasProjectsNavBeta = useBetaFeatureFlag(Feature.projectsNavBeta);
  const isMobile = useIsMobile();

  useEffect(() => {
    let selectionFrame: number | null = null;
    const focusFrame = requestAnimationFrame(() => {
      setFocus("name");
      selectionFrame = requestAnimationFrame(() => {
        const input = nameInputRef.current;
        if (input) {
          const caretPosition = input.value.length ?? 0;
          input.setSelectionRange(caretPosition, caretPosition);
        }
      });
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      if (selectionFrame !== null) {
        cancelAnimationFrame(selectionFrame);
      }
    };
  }, [setFocus]);

  useEffect(() => {
    if (titleError) {
      setError("name", { message: titleError });
      previousNameRef.current = name;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleError, setError]);

  useEffect(() => {
    if (formState.errors.name && previousNameRef.current !== undefined && name !== previousNameRef.current) {
      clearErrors("name");
      onErrorDismiss?.();
      previousNameRef.current = undefined;
    }
  }, [name, formState.errors.name, clearErrors, onErrorDismiss]);

  const { onUpload, onDelete } = useProjectUploadCallbacks();

  const onSubmit = useCallback(
    async (data: ProjectFormFields) => {
      if (isImageUploading) return;
      await handleAction({
        title: data.name,
        developmentType: [data.developmentType],
        goal: data.goal ?? "",
        image: data.image ?? undefined,
      });
    },
    [handleAction, isImageUploading]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLFormElement>) => {
      if (event.key === "Enter") {
        const isTextarea = (event.target as HTMLElement).tagName === "TEXTAREA";
        if (event.metaKey || event.ctrlKey) {
          event.preventDefault();
          handleSubmit(onSubmit)();
        } else if (!isTextarea) {
          event.preventDefault();
        }
      }
    },
    [handleSubmit, onSubmit]
  );

  return (
    <ModalV2.Root defaultOpen={true} onOpenChange={onClose}>
      <ModalV2.Body
        titleSlot={title}
        className="z-99999!"
        contentClassName={clsx("w-[540px]!", hasProjectsNavBeta && isMobile && "h-dvh max-h-dvh")}
        disableClose={loading}
        hideClose={loading}
      >
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className="flex flex-col gap-y-6">
          <FormSection label={t("projects.modal.workspace_image")} optional>
            <Controller
              name="image"
              control={control}
              render={({ field }) => (
                <>
                  <ImageUpload
                    value={project?.imageUrl ?? undefined}
                    onChange={(value) => field.onChange(value ?? null)}
                    onUpload={onUpload}
                    onDelete={onDelete}
                    onStatusChange={setImageUploadStatus}
                    disabled={loading}
                    height={160}
                  />
                  {hasImageUploadError && (
                    <p className="m-0 text-xs text-red-700" role="alert">
                      {t("projects.modal.image_upload_error")}
                    </p>
                  )}
                </>
              )}
            />
          </FormSection>
          <FormSection label={t("projects.modal.workspace_name")}>
            <div className="flex gap-x-2 items-start">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextInputV2
                    size="large"
                    className="grow"
                    {...field}
                    ref={(e) => {
                      field.ref(e);
                      nameInputRef.current = e;
                    }}
                    type="text"
                    disabled={loading}
                    maxLength={MAX_PROJECT_NAME_LENGTH}
                    errorMessage={formState.errors.name?.message}
                  />
                )}
              />
            </div>
          </FormSection>
          <FormSection label={t("projects.modal.development_type")}>
            <Controller
              name="developmentType"
              control={control}
              render={({ field }) => (
                <ToggleGroupChips
                  type="single"
                  value={field.value}
                  onValueChange={(value) => field.onChange(value as DevelopmentType)}
                  disabled={loading}
                >
                  {DEVELOPMENT_TYPES.map(({ value, label, icon, iconColour }) => (
                    <ToggleGroupChips.Item
                      key={value}
                      value={value}
                      label={label}
                      icon={<Icon size="sm" cursor="pointer" icon={icon} extraClasses={iconColour} />}
                    />
                  ))}
                </ToggleGroupChips>
              )}
            />
            {formState.errors.developmentType && (
              <div className="pt-1 m-0 text-xs font-normal leading-5 text-red-700">
                {formState.errors.developmentType.message}
              </div>
            )}
          </FormSection>
          <FormSection label={t("projects.modal.workspace_goal")} optional>
            <Controller
              name="goal"
              control={control}
              render={({ field }) => (
                <Textarea
                  placeholder="E.g. Identify underused industrial land on the edge of an urban centre in the South West for a 20–30 unit residential scheme with ground-floor commercial use and active street frontage."
                  maxLength={MAX_PROJECT_GOAL_LENGTH}
                  defaultValue={field.value}
                  onChange={field.onChange}
                  debounceValue={50}
                  className="grow max-h-60"
                  disabled={loading}
                />
              )}
            />
          </FormSection>
          <div className="flex items-center justify-end gap-x-3 mt-3">
            {genericError && <span className="text-xs text-text-accent-red">{t("projects.modal.generic_error")}</span>}
            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={loading || !formState.isValid || isImageUploading}
              aria-busy={isImageUploading}
            >
              {actionLabel}
            </Button>
          </div>
        </form>
      </ModalV2.Body>
    </ModalV2.Root>
  );
};

const FormSection = ({ label, optional, children }: PropsWithChildren<{ label: string; optional?: boolean }>) => (
  <div className="flex flex-col gap-y-2">
    <span className="text-sm font-normal leading-5 text-text-base-primary">
      {label} {optional && <span className="text-text-base-tertiary">(optional)</span>}
    </span>
    {children}
  </div>
);
